import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import AnimatedText from '../AnimatedText';
import { getClassificationColor } from '../../utils/classificationUtils';

export interface FavoritePlayer {
  id?: string;
  user_id: string;
  name: string;
  link: string;
  club?: string;
  elo?: number;
  classification?: string;
  license_number?: string;
  created_at?: string;
}

interface FavoritesDropdownProps {
  favorites: FavoritePlayer[];
  onFavoriteClick: (link: string) => void;
  t: {
    yourFavorites: string;
  };
}

/**
 * FavoritesDropdown Component
 * Displays user's favorite players when search is focused but empty
 */
const FavoritesDropdown: React.FC<FavoritesDropdownProps> = ({
  favorites,
  onFavoriteClick,
  t
}) => {
  // Format player name
  const formatPlayerName = (name: string) => {
    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(s => s.trim());
      return `${firstName} ${lastName}`;
    }
    return name;
  };

  return (
    <motion.div
      className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-300 dark:border-gray-700 max-h-96 overflow-y-auto z-50 transition-colors duration-300"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600 transition-colors duration-300">
        <AnimatedText
          text={t.yourFavorites}
          className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left transition-colors duration-300"
          delay={0.1}
          staggerDelay={0.02}
        />
      </div>
      {favorites.map((favorite, index) => (
        <motion.div
          key={favorite.id}
          className="group"
          initial={{ opacity: 0, x: -20 }}
          animate={{
            opacity: 1,
            x: 0
          }}
          transition={{
            duration: 0.3,
            delay: 0.1 + index * 0.05,
            ease: [0.25, 0.1, 0.25, 1.0]
          }}
        >
          <motion.div
            onClick={() => onFavoriteClick(favorite.link)}
            className="px-6 py-4 cursor-pointer transition-all border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            whileHover={{
              transition: { duration: 0.2 }
            }}
          >
            <div className="flex items-start justify-between">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 + 0.1 }}
                className="flex-1 min-w-0 mr-3 text-left"
              >
                <motion.p
                  className="font-medium text-gray-900 dark:text-gray-100 truncate text-left transition-colors duration-300"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 + 0.15 }}
                >
                  {formatPlayerName(favorite.name)}
                </motion.p>
                {favorite.club && (
                  <motion.p
                    className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1 text-left transition-colors duration-300"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 + 0.2 }}
                  >
                    {favorite.club}
                  </motion.p>
                )}
              </motion.div>
              <motion.div
                className="flex items-center gap-2 flex-shrink-0"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: index * 0.05 + 0.25,
                  type: "spring",
                  stiffness: 200,
                  damping: 15
                }}
              >
                <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                {favorite.classification && (() => {
                  const colors = getClassificationColor(favorite.classification);
                  return (
                    <motion.span
                      className="text-xs font-bold px-2.5 py-1 rounded-lg"
                      style={{
                        backgroundColor: colors.bg,
                        color: colors.text
                      }}
                      whileHover={{
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                    >
                      {favorite.classification}
                    </motion.span>
                  );
                })()}
                {favorite.elo && (
                  <motion.span
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg whitespace-nowrap transition-colors duration-300"
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {favorite.elo}
                  </motion.span>
                )}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default FavoritesDropdown;
